/**
 * fieldValueFormatter.js
 *
 * Unified field value formatting utility for all report display views.
 * Replaces scattered formatting logic across GenericTableView, GenericPivotTable,
 * GenericNumericalView, and excelFormatters.
 *
 * Usage:
 *   const annotationMap = buildAnnotationMap(resultSet);
 *   const formatter = createFieldFormatter(columnKey, annotationMap, { intl, numberFormat, dateFormat });
 *   formatter(item) → formatted value
 */

import React from 'react';
import moment from 'moment';
import {
  translateReferenceValue,
} from './referenceTableTranslations';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Cube.js field types as returned in annotation */
export const FIELD_TYPES = {
  STRING: 'string',
  NUMBER: 'number',
  TIME: 'time',
  BOOLEAN: 'boolean',
};

/** Default date/number formats used when no formatting prop is provided */
export const DEFAULT_FORMATS = {
  date: 'YYYY-MM-DD',
  number: '#,##0',
};

// ─────────────────────────────────────────────────────────────────────────────
// Annotation helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a flat map of { columnKey_with_underscore → fieldType } from resultSet annotation.
 * Keys use underscore notation (dots replaced) to match column keys in display views.
 *
 * @param {object} resultSet
 * @returns {object} e.g. { "cataloging_view_total_holdings": "number", "cataloging_view_catalogeddate": "time" }
 */
export const buildAnnotationMap = (resultSet) => {
  const annotation = resultSet?.loadResponses?.[0]?.annotation || {};
  const map = {};

  Object.entries(annotation.dimensions || {}).forEach(([key, meta]) => {
    map[key.replace('.', '_')] = { type: meta.type, meta: meta.meta || null };
  });

  Object.entries(annotation.measures || {}).forEach(([key, meta]) => {
    map[key.replace('.', '_')] = {
      type: meta.type,
      meta: meta.meta || null,
      drillMembers: meta.drillMembers || null,
    };
  });

  return map;
};

// ─────────────────────────────────────────────────────────────────────────────
// Primitive formatters
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a numeric value.
 * @param {*}      value
 * @param {object} options
 * @param {string} [options.numberFormat]   - Pattern like '#,##0' or '#,##0.00'
 * @param {string} [options.currencySymbol] - e.g. '$', '﷼'
 * @returns {string}
 */
export const formatNumber = (value, options = {}) => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);

  const { numberFormat = DEFAULT_FORMATS.number, currencySymbol = '' } =
    options;
  const withDecimals = numberFormat.includes('.');
  const formatted = withDecimals
    ? num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : num.toLocaleString(undefined, { maximumFractionDigits: 0 });

  return currencySymbol ? `${currencySymbol}${formatted}` : formatted;
};

/**
 * Format a date/time value.
 * Handles ISO 8601 strings and moment-compatible values.
 * @param {*}      value
 * @param {object} options
 * @param {string} [options.dateFormat] - moment format string e.g. 'YYYY-MM-DD', 'DD/MM/YYYY'
 * @returns {string}
 */
export const formatDate = (value, options = {}) => {
  if (!value) return '';
  const { dateFormat = DEFAULT_FORMATS.date } = options;
  const m = moment(value).locale('en');
  return m.isValid() ? m.format(dateFormat) : String(value);
};

/**
 * Format a boolean value using intl if available.
 * @param {*}      value
 * @param {object} options
 * @param {object} [options.intl] - react-intl intl object
 * @returns {string}
 */
export const formatBoolean = (value, options = {}) => {
  if (value === null || value === undefined) return '';
  const { intl } = options;
  const boolVal =
    value === true || value === 'true' || value === '1' || value === 1;

  if (intl) {
    return intl.formatMessage({
      id: boolVal ? 'ui-reports.common.yes' : 'ui-reports.common.no',
      defaultMessage: boolVal ? 'Yes' : 'No',
    });
  }
  return boolVal ? 'Yes' : 'No';
};

/**
 * Format an array or object value as a readable string.
 * Arrays → comma-separated values
 * Objects → JSON string
 * @param {*} value
 * @returns {string}
 */
export const formatArrayOrObject = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'object' ? JSON.stringify(item) : String(item),
      )
      .join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

// ─────────────────────────────────────────────────────────────────────────────
// Core dispatcher
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a value based on its Cube.js field type.
 * Falls back to auto-detection if type is unknown.
 *
 * @param {*}      value
 * @param {string} fieldType  - One of FIELD_TYPES (string|number|time|boolean)
 * @param {object} options
 * @param {string} [options.numberFormat]
 * @param {string} [options.currencySymbol]
 * @param {string} [options.dateFormat]
 * @param {object} [options.intl]
 * @returns {*} formatted value (string or React element for boolean)
 */
export const formatByType = (value, fieldType, options = {}) => {
  if (value === null || value === undefined || value === '') return value;

  // Array / object — regardless of declared type
  if (Array.isArray(value) || typeof value === 'object') {
    return formatArrayOrObject(value);
  }

  switch (fieldType) {
    case FIELD_TYPES.NUMBER:
      return formatNumber(value, options);

    case FIELD_TYPES.TIME:
      return formatDate(value, options);

    case FIELD_TYPES.BOOLEAN:
      return formatBoolean(value, options);

    case FIELD_TYPES.STRING:
    default:
      // Auto-detect ISO date strings even when declared as string
      if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
      ) {
        return formatDate(value, options);
      }
      return value;
  }
};

/**
 * Format a comma-separated reference list by translating each item.
 * Used for fields like instance_formats_list, nature_of_content_list.
 *
 * @param {string} value       - e.g. "Computer file, Electronic resource"
 * @param {string} cubeName    - e.g. "instance_format"
 * @param {string} fieldName   - e.g. "name"
 * @param {object} intl        - react-intl intl object
 * @param {string} [separator] - default ', '
 * @returns {string} translated list
 */
/**
 * Format a value as a hyperlink using a linkTemplate.
 * Template placeholders {fieldName} are replaced with values from the item row.
 *
 * @param {string} value         - Display text
 * @param {string} linkTemplate  - e.g. '/inventory/view/{cataloging_view_instance_id}'
 * @param {object} item          - Full row data
 * @returns {React.Element}
 */
export const formatLink = (value, linkTemplate, item) => {
  if (!value || !linkTemplate) return value;
  const url = linkTemplate.replace(/\{(\w+)\}/g, (_, key) => item[key] || '');
  return React.createElement('a', { href: url, target: '_blank', rel: 'noopener noreferrer' }, value);
};

export const formatReferenceList = (value, cubeName, fieldName, intl, separator = ', ') => {
  if (!value || !intl) return value;
  return value
    .split(separator)
    .map(item => translateReferenceValue(intl, cubeName, fieldName, item.trim()))
    .join(separator);
};

// ─────────────────────────────────────────────────────────────────────────────
// Main factory — used by display views
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create a formatter function for a specific column key.
 * Looks up the field type from annotationMap, then applies formatByType.
 *
 * @param {string} columnKey     - Column key with underscores e.g. "cataloging_view_total_holdings"
 * @param {object} annotationMap - Built by buildAnnotationMap()
 * @param {object} options       - { numberFormat, currencySymbol, dateFormat, intl }
 * @returns {function} (item) => formattedValue
 *
 * @example
 * const annotationMap = buildAnnotationMap(resultSet);
 * const fmt = createFieldFormatter('cataloging_view_total_holdings', annotationMap, { numberFormat: '#,##0' });
 * fmt({ cataloging_view_total_holdings: 1234567 }) // → "1,234,567"
 */
export const createFieldFormatter = (
  columnKey,
  annotationMap = {},
  options = {},
) => {
  const entry = annotationMap[columnKey] || {};
  const fieldType = entry.type || null;
  const fieldMeta = entry.meta || null;

  // Hyperlink — wrap value in <a> using linkTemplate
  if (fieldMeta?.linkTemplate) {
    return (item) => formatLink(item[columnKey], fieldMeta.linkTemplate, item);
  }

  // Reference list — translate each item in the comma-separated string
  if (fieldMeta?.isReferenceList && options.intl) {
    return (item) => formatReferenceList(
      item[columnKey],
      fieldMeta.referenceCube,
      fieldMeta.referenceField,
      options.intl,
    );
  }

  // Single reference value — translate directly
  if (fieldMeta?.isReference && options.intl) {
    return (item) => translateReferenceValue(
      options.intl,
      fieldMeta.referenceCube,
      fieldMeta.referenceField,
      item[columnKey],
    );
  }

  return (item) => formatByType(item[columnKey], fieldType, options);
};

/**
 * Build formatting options object from a formatting prop (saved report visualization settings).
 * Falls back to defaults when formatting prop is null (query builder mode).
 *
 * @param {object|null} formatting - From reportData.visualization.formatting
 * @param {object}      [intl]     - react-intl intl object
 * @returns {object} options ready for formatByType / createFieldFormatter
 */
export const buildFormattingOptions = (formatting, intl) => ({
  numberFormat: formatting?.numberFormat || DEFAULT_FORMATS.number,
  dateFormat: formatting?.dateFormat || DEFAULT_FORMATS.date,
  currencySymbol: formatting?.currencyFormat?.symbol || '',
  intl,
});

// ─────────────────────────────────────────────────────────────────────────────
// Reference field detection & translation
// ─────────────────────────────────────────────────────────────────────────────
